/**
 * @format
 */

import React, { useState } from 'react';
import { Table, Button, Spin } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { useWeeklyLeaderboard } from '../hooks/useLeaderboard';
import { WeeklyLeaderboardItem } from '../types/leaderboard.types';

interface WeeklyLeaderboardProps {
  filter?: any;
  pollInterval?: number;
}

const WeeklyLeaderboard: React.FC<WeeklyLeaderboardProps> = ({ filter, pollInterval }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data, loading, error } = useWeeklyLeaderboard(filter, pollInterval);

  // Process data: data is 7-element array where each element is array of items for that day
  const processedData: Record<string, any> = {};
  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Flatten and organize data by user
  data.forEach((dayArray, dayIndex) => {
    dayArray.forEach((item: WeeklyLeaderboardItem) => {
      const userId = item.userId;
      if (!processedData[userId]) {
        processedData[userId] = {
          userId,
          name: item.user?.firstName && item.user?.lastName 
            ? `${item.user.firstName} ${item.user.lastName}`
            : 'Unknown',
          totalMajorMed: Array(7).fill(0),
          totalPrivateMed: Array(7).fill(0),
          totalAncillaries: Array(7).fill(0),
          premiums: Array(7).fill(0),
          totalSales: 0,
          totalPremium: 0,
        };
      }

      processedData[userId].totalMajorMed[dayIndex] = item.totalMajorMed;
      processedData[userId].totalPrivateMed[dayIndex] = item.totalPrivateMed;
      processedData[userId].totalAncillaries[dayIndex] = item.totalAncillaries || 0;
      processedData[userId].premiums[dayIndex] = item.totalPremium;
      processedData[userId].totalSales += item.totalMajorMed + item.totalPrivateMed + item.totalAncillaries;
      processedData[userId].totalPremium += item.totalPremium;
    });
  });

  // Convert to array and sort by total premium
  const agentsData = Object.values(processedData).sort(
    (a: any, b: any) => b.totalPremium - a.totalPremium
  );

  // Calculate daily totals
  const dailyTotals = {
    totalMajorMed: Array(7).fill(0),
    totalPrivateMed: Array(7).fill(0),
    totalAncillaries: Array(7).fill(0),
    premiums: Array(7).fill(0),
  };

  agentsData.forEach((agent: any) => {
    agent.totalMajorMed.forEach((value: number, index: number) => {
      dailyTotals.totalMajorMed[index] += value;
    });
    agent.totalPrivateMed.forEach((value: number, index: number) => {
      dailyTotals.totalPrivateMed[index] += value;
    });
    agent.totalAncillaries.forEach((value: number, index: number) => {
      dailyTotals.totalAncillaries[index] += value;
    });
    agent.premiums.forEach((value: number, index: number) => {
      dailyTotals.premiums[index] += value;
    });
  });

  const columns = [
    {
      title: 'Agent',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left' as const,
      className: 'agent-column',
    },
    ...daysOfWeek.map((day, dayIndex) => ({
      title: day,
      children: [
        {
          title: 'ACA',
          dataIndex: ['totalMajorMed', dayIndex],
          key: `${day}-majormed`,
          render: (_: any, record: any) => record.totalMajorMed[dayIndex] || 0,
          className: 'majormed-column',
        },
        {
          title: 'U65',
          dataIndex: ['totalPrivateMed', dayIndex],
          key: `${day}-privatemed`,
          render: (_: any, record: any) => record.totalPrivateMed[dayIndex] || 0,
          className: 'privatemed-column',
        },
        {
          title: 'Anc',
          dataIndex: ['totalAncillaries', dayIndex],
          key: `${day}-ancillaries`,
          render: (_: any, record: any) => record.totalAncillaries[dayIndex] || 0,
          className: 'ancillaries-column',
        },
        {
          title: 'Prem',
          dataIndex: ['premiums', dayIndex],
          key: `${day}-premiums`,
          render: (_: any, record: any) =>
            `$${record.premiums[dayIndex] ? record.premiums[dayIndex].toFixed(2) : '0.00'}`,
          className: 'premiums-column',
        },
      ],
    })),
    {
      title: 'Total',
      children: [
        {
          title: 'ACA',
          dataIndex: 'totalMajorMed',
          key: 'total-major-med',
          render: (totalMajorMed: number[]) =>
            totalMajorMed.reduce((acc, val) => acc + val, 0),
        },
        {
          title: 'U65',
          dataIndex: 'totalPrivateMed',
          key: 'total-private-med',
          render: (totalPrivateMed: number[]) =>
            totalPrivateMed.reduce((acc, val) => acc + val, 0),
          className: 'total-privatemed-column',
        },
        {
          title: 'Anc',
          dataIndex: 'totalAncillaries',
          key: 'total-ancillaries',
          render: (totalAncillaries: number[]) =>
            totalAncillaries.reduce((acc, val) => acc + val, 0),
        },
        {
          title: 'Prem',
          dataIndex: 'totalPremium',
          key: 'total-premium',
          render: (totalPremium: number) => `$${totalPremium.toFixed(2)}`,
          className: 'total-premium-column',
        },
      ],
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin tip='Loading weekly leaderboard...' />
      </div>
    );
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error loading weekly leaderboard: {error.message}</div>;
  }

  return (
    <div
      className={`leaderboard-container ${isExpanded ? 'expanded' : 'normal'}`}
      style={{
        position: isExpanded ? 'fixed' : 'relative',
        padding: '10px',
        top: '0px',
        left: '0px',
        height: isExpanded ? '100vh' : 'auto',
        width: isExpanded ? '100vw' : 'auto',
        overflow: isExpanded ? 'auto' : 'hidden',
        zIndex: isExpanded ? 9999 : 'auto',
        background: '#fff',
      }}>
      <Button
        type='primary'
        icon={isExpanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 10000,
        }}
      />

      <Table
        dataSource={agentsData}
        columns={columns}
        rowKey={(record) => record.userId}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 'max-content' }}
        bordered
        className='leaderboard-table'
        summary={() => {
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                {daysOfWeek.map((day, dayIndex) => (
                  <React.Fragment key={day}>
                    <Table.Summary.Cell index={dayIndex * 4 + 1}>
                      {dailyTotals.totalMajorMed[dayIndex]}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={dayIndex * 4 + 2}>
                      {dailyTotals.totalPrivateMed[dayIndex]}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={dayIndex * 4 + 3}>
                      {dailyTotals.totalAncillaries[dayIndex]}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={dayIndex * 4 + 4}>
                      ${dailyTotals.premiums[dayIndex].toFixed(2)}
                    </Table.Summary.Cell>
                  </React.Fragment>
                ))}
                <Table.Summary.Cell index={28}>
                  {dailyTotals.totalMajorMed.reduce((acc, val) => acc + val, 0)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={29}>
                  {dailyTotals.totalPrivateMed.reduce((acc, val) => acc + val, 0)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={30}>
                  {dailyTotals.totalAncillaries.reduce((acc, val) => acc + val, 0)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={31}>
                  ${dailyTotals.premiums.reduce((acc, val) => acc + val, 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>

              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Average Premium</Table.Summary.Cell>
                {daysOfWeek.map((day, dayIndex) => (
                  <React.Fragment key={day}>
                    <Table.Summary.Cell index={dayIndex * 4 + 1} />
                    <Table.Summary.Cell index={dayIndex * 4 + 2} />
                    <Table.Summary.Cell index={dayIndex * 4 + 3} />
                    <Table.Summary.Cell index={dayIndex * 4 + 4}>
                      {(() => {
                        const totalSales =
                          dailyTotals.totalMajorMed[dayIndex] +
                          dailyTotals.totalPrivateMed[dayIndex] +
                          dailyTotals.totalAncillaries[dayIndex];
                        const totalPremiums = dailyTotals.premiums[dayIndex];
                        const averagePremium = totalSales > 0 ? totalPremiums / totalSales : 0;
                        return `$${averagePremium.toFixed(2)}`;
                      })()}
                    </Table.Summary.Cell>
                  </React.Fragment>
                ))}
                <Table.Summary.Cell index={28} />
                <Table.Summary.Cell index={29} />
                <Table.Summary.Cell index={30} />
                <Table.Summary.Cell index={31}>
                  {(() => {
                    const totalSales =
                      dailyTotals.totalMajorMed.reduce((acc, val) => acc + val, 0) +
                      dailyTotals.totalPrivateMed.reduce((acc, val) => acc + val, 0) +
                      dailyTotals.totalAncillaries.reduce((acc, val) => acc + val, 0);
                    const totalPremiums = dailyTotals.premiums.reduce((acc, val) => acc + val, 0);
                    const averagePremium = totalSales > 0 ? totalPremiums / totalSales : 0;
                    return `$${averagePremium.toFixed(2)}`;
                  })()}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
      />
    </div>
  );
};

export default WeeklyLeaderboard;
